import { useState } from 'react';
import { selectCurrentUser } from 'features/auth/slice';
import { useSelector } from 'react-redux';

import useComments from 'lib/hooks/useComments';

import {
  flash,
  formatTimeAgo,
  Markdown,
  PopoverMenu,
  recordsMatch,
} from '@frontendmentorio/design-system-v2';

import UserDetails from 'features/learners/UserDetails';
import ConfirmPopoverMenuItem from 'features/shared/ConfirmPopoverMenuItem';
import AdminDeleteButton from 'features/shared/AdminDeleteButton';
import CommentForm from './Form';

export default function Content({ children, comment }) {
  const { remove, update } = useComments();
  const currentUser = useSelector(selectCurrentUser);
  const isAuthor = recordsMatch(comment.user, currentUser);

  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleRemove = async () => {
    try {
      await remove(comment);
    } catch (error) {
      flash.error(error.message);
    }
  };

  const handleUpdate = async (data) => {
    setIsLoading(true);

    try {
      await update(comment, data);
      setIsEdit(false);
    } catch (error) {
      flash.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4" id={`comment-${comment.id}`}>
      <div className="flex flex-wrap items-center gap-3 w-full">
        <UserDetails
          className="mr-auto overflow-x-hidden pt-1"
          user={comment.user}
        />

        <time className="italic text-neutral-500 text-sm">
          Posted {formatTimeAgo(comment.createdAt)}
        </time>

        {currentUser?.admin && !isAuthor && (
          <AdminDeleteButton
            modal={{
              button: 'Delete comment',
              description:
                'This will permanently delete this comment **and remove any associated points**.',
              title: 'One last check! 😅',
            }}
            onClick={handleRemove}
          />
        )}

        {isAuthor && comment.content && (
          <PopoverMenu
            className="bg-neutral-50 border border-gray-100 rounded-full"
            buttonClassName="flex flex-center h-[2rem] w-[2rem] *:text-lg *:text-neutral-500"
            name="comment options"
          >
            <PopoverMenu.Item onClick={() => setIsEdit(!isEdit)}>
              Edit comment
            </PopoverMenu.Item>
            <ConfirmPopoverMenuItem
              className="text-rose-600"
              modal={{
                button: 'Delete comment',
                description:
                  'This will permanently delete your comment **and remove any awarded points from your Mentor Score**.',
              }}
              onClick={handleRemove}
            >
              Delete comment
            </ConfirmPopoverMenuItem>
          </PopoverMenu>
        )}
      </div>

      <div>
        {isEdit ? (
          <CommentForm
            ariaLabel="Edit your comment"
            buttonText={isLoading ? 'Submitting...' : 'Save'}
            defaultValue={comment.content}
            disabled={isLoading}
            onSubmit={handleUpdate}
          />
        ) : comment.content ? (
          <Markdown>{comment.content}</Markdown>
        ) : (
          <p className="italic text-neutral-500">
            This comment was deleted {formatTimeAgo(comment.updatedAt)}
          </p>
        )}
      </div>

      {children}
    </div>
  );
}
